import { APIError, APIErrorType, useGetRetailersForOrganizationQuery } from '@vizit/api';
import { useAuth } from '@vizit/auth';
import { DEFAULT_RETAILER_REGION_ID, FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { CreateANewProductDialog } from '@vizit/organisms';
import * as React from 'react';
import { useProductCategoryList, useProductNewDialog } from './product-new-dialog.hooks';

interface ProductNewDialogProps {
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The ID of the portfolio to use for the upload
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The ID of the organization to use for the upload
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * The function to call when the PDP is successfully submitted
   *
   * @param pdpId - The ID of the PDP
   */
  onSuccessfulCreate: (pdpId: string) => void;
  /**
   * The function to call when the authentication error occurs
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: APIError) => void;
}

function ProductNewDialog(props: ProductNewDialogProps) {
  const {
    isOpen,
    onOpenChange,
    hideTrigger = false,
    workspaceId,
    organizationId,
    useIcon = false,
    onSuccessfulCreate,
    onAuthenticationError,
    ...other
  } = props;

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const { data: retailers, error: retailersError } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId ?? '' },
    { enabled: isRetailerEnabled && !!organizationId },
  );

  React.useEffect(() => {
    if (
      retailersError instanceof APIError &&
      (retailersError.type === APIErrorType.NotAuthenticated || retailersError.type === APIErrorType.NotAuthorized)
    ) {
      onAuthenticationError(retailersError);
    }
  }, [retailersError, onAuthenticationError]);

  const retailerSelectItems = isRetailerEnabled ? retailers : undefined;

  const { defaultRetailer } = useAuth();
  const [selectedRetailerId, setSelectedRetailerId] = React.useState<string>(
    defaultRetailer ?? DEFAULT_RETAILER_REGION_ID,
  );

  React.useEffect(() => {
    setSelectedRetailerId(defaultRetailer ?? DEFAULT_RETAILER_REGION_ID);
  }, [defaultRetailer]);

  const productCategoryComboBoxProps = useProductCategoryList({
    organizationId,
    workspaceId,
    retailerId: selectedRetailerId,
    onAuthenticationError,
  });

  const { hasOnSubmissionError, uploadStatus, onFileStatusChange, onSubmit } = useProductNewDialog({
    workspaceId,
    organizationId,
    onSuccessfulCreate,
    onAuthenticationError,
  });

  return (
    <CreateANewProductDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideTrigger={hideTrigger}
      hasOnSubmissionError={hasOnSubmissionError}
      retailers={retailerSelectItems}
      productCategoryComboBoxProps={productCategoryComboBoxProps}
      uploadStatus={uploadStatus}
      useIcon={useIcon}
      onFileStatusChange={onFileStatusChange}
      onRetailerChange={setSelectedRetailerId}
      onSubmit={onSubmit}
      {...other}
    />
  );
}

export { ProductNewDialog };
